import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { ImageGalleryStyles } from './ImageGallery.styled';
import { desktop_breakpoint } from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';
import { formatHttpsUrl } from '@utils/utils';
import StArrowRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/st-arrow-right.svg';
import { formatVirtTourRef } from '@utils/virtualTourUtils';
import { Carousel } from 'react-responsive-carousel';
import { DXFavoritesContext } from '@contexts/DXFavoritesContext';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import FavoritingHeart from '@components/Shared/Favoriting/FavoritingHeart';
import { imageBtnOptions } from '../HomeDetails';

const Modal = dynamic(() => import('@components/Shared/Modal/Modal'), {
  ssr: false,
});

const GalleryModal = dynamic(
  () =>
    import('@components/HomeDetails/ImageGallery/GalleryModal/GalleryModal'),
  { ssr: false }
);

const ImageGallery = ({ home, imageBtnSelection, clearImageBtnSelection }) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modelIndex, setModelIndex] = useState();
  const [isMobile, setIsMobile] = useState(true);
  const [show3DTourModal, setShow3DTourModal] = useState(false);
  const favoritesContext = useContext(DXFavoritesContext);

  const virtualTourReference = home.hasVirtualTour
    ? home.virtualTours[0].virtualTourReference
    : '';

  const images = [...home.images];
  if (home.hasVirtualTour) {
    images.splice(3, 0, home.virtualTours[0]); // adds virtual tour as 4th item
  }

  // Make the floorplan image 3rd if it isn't. If a floorplan image exists AND if the images array has at least three images.
  const hasFloorplan = images?.findIndex((i) => i?.imageType === 4);
  if (images?.length > 2 && hasFloorplan >= 0 && hasFloorplan != 2) {
    const fpimg = images.splice(hasFloorplan, 1); // remove floorplan from original location
    const oldimg = images.splice(2, 1, fpimg[0]); // add floorplan as 3rd item and remove existing item
    images.push(oldimg[0]); // add the image that was 3rd to the end of array
  }

  const homeCopy = { ...home, images };
  const virtualTourIndex = images?.findIndex((i) => i?.virtualTourReference);
  const floorplanIndex = images?.findIndex((i) => i?.imageType === 4);

  useWindowResize(() => {
    if (window.innerWidth >= 960) {
      setIsMobile(false);
    } else if (window.innerWidth >= desktop_breakpoint) {
      setIsMobile(false);
    } else if (window.innerWidth >= 375) {
      setIsMobile(true);
    } else if (window.innerWidth >= 345) {
      setIsMobile(true);
    } else {
      setIsMobile(true);
    }
  }, [345, 375, 960, desktop_breakpoint]);

  const handleClick = (e, index) => {
    e.preventDefault();
    setShowModal(true);
    setModelIndex(index);
  };

  const handleTourClick = (e) => {
    e.preventDefault();
    setShow3DTourModal(true);
  };

  useEffect(() => {
    if (
      imageBtnSelection === imageBtnOptions.virtTour ||
      imageBtnSelection === imageBtnOptions.floorplan
    ) {
      const imgIndex =
        imageBtnSelection === imageBtnOptions.virtTour
          ? virtualTourIndex
          : floorplanIndex;
      setModelIndex(imgIndex > -1 ? imgIndex : null);
      setShowModal(true);
    }
  }, [imageBtnSelection, virtualTourIndex, floorplanIndex]);

  return (
    <ImageGalleryStyles>
      <div className="image-gallery">
        <div className="single">
          <div className="image-gallery-card">
            <div className="absolute right-0 top-0 z-10">
              <FavoritingHeart
                className="cursor-pointer p-6 pt-4 md:pt-6"
                isFavorited={
                  !!favoritesContext.state.favoriteModels?.filter(
                    (faveModels) =>
                      faveModels.modelNumber === homeCopy.modelNumber
                  )[0]
                }
                onClick={() => {
                  favoritesContext.actions.toggleFavoriteModel(
                    homeCopy.modelNumber
                  );
                  pushGTMEvent('clickFavoritesHeart');
                }}
              />
            </div>
            <Carousel
              showArrows={false}
              showIndicators={false}
              showStatus={false}
              showThumbs={false}
              onChange={(i) => setCarouselIndex(i)}
              infiniteLoop={true}
              renderArrowPrev={(onClickHandler) => (
                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center">
                  <div
                    onClick={onClickHandler}
                    className="pointer-events-auto cursor-pointer select-none p-6"
                  >
                    <div
                      className="grid size-12 place-content-center rounded-full bg-white"
                      style={{
                        boxShadow:
                          '0px 10px 17px 0px rgba(0, 0, 0, 0.15), 0px 3px 15px 0px rgba(0, 0, 0, 0.15)',
                      }}
                    >
                      <Image
                        src="/public-assets/images/chevron-left-24x24.svg"
                        width={24}
                        height={24}
                      />
                    </div>
                  </div>
                </div>
              )}
              renderArrowNext={(onClickHandler) => (
                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center">
                  <div
                    onClick={onClickHandler}
                    className="pointer-events-auto cursor-pointer select-none p-6"
                  >
                    <div
                      className="grid size-12 place-content-center rounded-full bg-white"
                      style={{
                        boxShadow:
                          '0px 10px 17px 0px rgba(0, 0, 0, 0.15), 0px 3px 15px 0px rgba(0, 0, 0, 0.15)',
                      }}
                    >
                      <Image
                        src="/public-assets/images/chevron-right-24x24.svg"
                        width={24}
                        height={24}
                      />
                    </div>
                  </div>
                </div>
              )}
            >
              {images.map((media, i) => {
                return (
                  <div
                    key={i}
                    className="aspect-[3/2] cursor-pointer"
                    onClick={(e) => handleClick(e, i)}
                  >
                    {media.virtualTourReference ? (
                      <div className="pointer-events-none h-full w-full">
                        <iframe
                          className="!m-0 !w-full"
                          title="matterport"
                          width="100%"
                          height="100%"
                          alt={media.thumbnailReference}
                          src={media.virtualTourReference}
                        />
                      </div>
                    ) : (
                      <Image
                        src={media.reference + '?w=722&h=482&fm=webp'}
                        alt={media.caption}
                        className="h-full w-full object-cover"
                        fill
                      />
                    )}
                  </div>
                );
              })}
            </Carousel>
            <div className="pointer-events-none absolute bottom-0 grid w-full place-content-center">
              <div className="flex items-center">
                <div className="ml-6 flex h-6 items-center gap-1 rounded-3xl bg-black px-2 py-1">
                  <Image
                    src="/public-assets/images/camera-16x16.svg"
                    width={16}
                    height={16}
                  />
                  <div className="font-sourceSans text-xs font-semibold text-white">
                    {carouselIndex + 1}/{images.length}
                  </div>
                </div>
                <div
                  className="pointer-events-auto cursor-pointer py-6 pl-2 pr-6"
                  onClick={(e) => handleClick(e, undefined)}
                >
                  <div className="rounded-3xl bg-black px-2 py-1 font-sourceSans text-xs font-semibold uppercase text-white">
                    View All
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className="quad">
            {images.slice(1, 4).map((media, index) => (
              <div
                key={'deck-' + '-card' + index}
                className="image-gallery-card"
              >
                {media?.reference && !media?.virtualTourReference && (
                  <div
                    className="quad-img cursor-pointer"
                    onClick={(e) => handleClick(e, index + 1)}
                  >
                    <Image
                      alt={media.caption}
                      src={formatHttpsUrl(
                        `${media.reference}?w=375&h=250&fm=webp`
                      )}
                      width={375}
                      height={250}
                    />
                  </div>
                )}
                {media?.virtualTourReference && (
                  <div
                    className="quad-img h-full w-full cursor-pointer"
                    onClick={(e) => handleClick(e, 3)}
                  >
                    <div className="pointer-events-none h-full w-full">
                      <iframe
                        title="matterport"
                        width="100%"
                        height="100%"
                        alt={media.thumbnailReference}
                        src={media.virtualTourReference}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="image-gallery-card">
              <div
                className="quad-img"
                onClick={(e) => handleClick(e, undefined)}
              >
                <Image
                  alt={homeCopy.images[0].caption}
                  src={formatHttpsUrl(
                    `${homeCopy.images[0].reference}?w=375&h=250&fm=webp`
                  )}
                  width={375}
                  height={250}
                />
                <div className="overlay-viewall">
                  <div>
                    View All <StArrowRightSvg />{' '}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <GalleryModal
          model={homeCopy}
          onClick={handleTourClick}
          onClose={() => {
            setShowModal(false);
            setModelIndex(undefined);
            clearImageBtnSelection();
          }}
          isMobile={isMobile}
          modelIndex={modelIndex}
        />
      )}
      {show3DTourModal && virtualTourReference && (
        <div className="tour-modal">
          {/* This is the modal that plays the matterport. */}
          <Modal onClose={() => setShow3DTourModal(false)}>
            <iframe
              title="Matterport 3D Tour"
              className="gtm-matterport-play"
              allowFullScreen
              allow="xr-spatial-tracking"
              src={formatVirtTourRef(virtualTourReference)}
              width="100%"
              height="100%"
            />
          </Modal>
        </div>
      )}
    </ImageGalleryStyles>
  );
};

export default ImageGallery;

ImageGallery.propTypes = {
  home: PropTypes.any,
  imageBtnSelection: PropTypes.string,
  clearImageBtnSelection: PropTypes.func,
};
